/**
 * Signs the Paradigm user into the DIME Terminal with their existing session:
 * the terminal package exchanges a Paradigm embed token for a DIME session
 * before any sign-in UI, so a linked user never sees Clerk. Verified but
 * unlinked identities are refused with `IDENTITY_NOT_LINKED`, the package
 * falls back to Clerk, and this context flags that linking is on offer.
 *
 * Lives above the terminal chrome because both pieces of state must outlive a
 * terminal mount: `silentExchange`'s object identity keys the package's
 * session cache (a new object per mount re-mints), and the drawer unmounts
 * `DimeCloudApp` every time it closes — a link prompt tracked below that
 * would reappear on every reopen.
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { createPlatformTokenExchange } from '@tradeparadigm/terminal-react';

import {
  getUser,
  getUserId,
  selectIsAuthenticated,
} from '@paradigm/store/auth';

import { mintDimeEmbedToken } from './embed-token';
import type { DimeCloudAppProps } from '@tradeparadigm/terminal-react';

type SilentExchange = NonNullable<DimeCloudAppProps['silentExchange']>;

interface TerminalNativeAuth {
  /** Handed straight to `DimeCloudApp`; stable for the life of the session. */
  readonly silentExchange: SilentExchange;
  /**
   * The exchange was refused because this Paradigm identity has no DIME link
   * and the user hasn't waved the offer away yet — the cue to render the
   * link prompt after the Clerk fallback.
   */
  readonly showLinkPrompt: boolean;
  /** Stop offering the link for the rest of this page load. */
  readonly dismissLinkPrompt: () => void;
}

const TerminalNativeAuthContext = createContext<TerminalNativeAuth | null>(
  null,
);

export function TerminalNativeAuthProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const userId = useSelector(getUserId);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  // Everything below is scoped to one account — the exchange, the session
  // cache key it resolves under, the link-prompt flags. This app can swap
  // users without a page load, so remount on the account rather than leaving
  // another user's state behind.
  //
  // Both signals are needed, the same pair `TerminalDrawerProvider` keys off:
  // the auth reducer resets `user.id` to `0` (not null) on logout, so an
  // id-only key would treat logged-out as a real account; and the
  // session-reuse saga can dispatch `loginSuccess` before React commits the
  // intermediate `id === 0`, so a boundary keyed on the id alone can be
  // batched away entirely.
  const accountId =
    isAuthenticated && userId !== 0 ? String(userId) : ANONYMOUS_SCOPE;

  return (
    <ScopedNativeAuth key={accountId} accountId={accountId}>
      {children}
    </ScopedNativeAuth>
  );
}

/**
 * Scope for a logged-out render. No terminal surface mounts without a session,
 * so this scope's exchange is never consumed — it exists to keep the
 * signed-out tree out of any real account's bucket.
 */
const ANONYMOUS_SCOPE = 'anon';

function ScopedNativeAuth({
  accountId,
  children,
}: {
  readonly accountId: string;
  readonly children: ReactNode;
}) {
  const user = useSelector(getUser);
  const [notLinked, setNotLinked] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const exchange = useMemo(
    () =>
      createPlatformTokenExchange(mintDimeEmbedToken, {
        // Fires only on the IDENTITY_NOT_LINKED refusal — a verified user with
        // no link. Every other failure (expired token, network, an
        // environment whose DIME trust isn't enabled yet) has no linking
        // story and stays a silent fall-through to Clerk.
        onNotLinked: () => setNotLinked(true),
      }),
    [],
  );

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined;
  const { email } = user;

  const silentExchange = useMemo<SilentExchange>(
    () => ({
      exchange,
      // Stable per user: this keys the package's session cache and its local
      // storage buckets.
      accountId,
      // Both fields are `''` rather than absent on an empty user, which would
      // render as a blank identity in the account menu.
      user: { displayName, email: email || undefined },
      // Enables the packaged "Unlink account" action in place of a Log Out
      // that would silently sign the user straight back in on the next mount.
      mintPlatformToken: mintDimeEmbedToken,
    }),
    [exchange, accountId, displayName, email],
  );

  const dismissLinkPrompt = useCallback(() => setDismissed(true), []);

  const value = useMemo<TerminalNativeAuth>(
    () => ({
      silentExchange,
      showLinkPrompt: notLinked && !dismissed,
      dismissLinkPrompt,
    }),
    [silentExchange, notLinked, dismissed, dismissLinkPrompt],
  );

  return (
    <TerminalNativeAuthContext.Provider value={value}>
      {children}
    </TerminalNativeAuthContext.Provider>
  );
}

/** Fails fast: a terminal surface mounted outside the provider would re-mint. */
export function useTerminalNativeAuth(): TerminalNativeAuth {
  const ctx = useContext(TerminalNativeAuthContext);
  if (ctx == null) {
    throw new Error(
      'useTerminalNativeAuth must be used within <TerminalNativeAuthProvider>',
    );
  }
  return ctx;
}
